<template>
    <MagTransition name="dialog" @after-leave="$emit('dialogClose')" appear>
        <KeepAlive>
            <dialog
                ref="dialogEl"
                v-if="!closed"
                :class="{
                    'mag-dialog': true,
                    'mag-dialog-popup': applyStyleToInner,
                    'low-backdrop': lowBackdrop,
                    'no-border': noBorder,
                    [`dialog-a-${currentAnimation}`]: true,
                    [`anchor-${anchor}`]: typeof anchor === 'string',
                    'anchor-dynamic': typeof anchor !== 'string',
                }"
                closedby="closerequest"
                @close="$emit('dialogClose')"
                :autofocus
                @click="onClick"
            >
                <Suspense>
                    <slot
                        @cancel="innerCancel"
                        @close="innerClosed"
                        class="mag-dialog-child"
                    ></slot>
                    <template #fallback>
                        <MkLoading />
                    </template>
                </Suspense>

                <MagPopoverLayer />
            </dialog>
        </KeepAlive>
    </MagTransition>
</template>
<script setup lang="ts">
import {
    onMounted,
    onWatcherCleanup,
    provide,
    ref,
    SetupContext,
    useTemplateRef,
    watchEffect,
} from "vue";
import { dialogAnimations, DialogProps } from "@/composables/modal";
import MagPopoverLayer from "@/components/layout/MagPopoverLayer.vue";
import { useElementSize } from "@vueuse/core";
import { placeRectBox } from "@/composables/mag-layout-composable";

const props = withDefaults(defineProps<DialogProps>(), {
    animation: "modal",
    anchor: "center",
});

provide("modal", true);

const closed = ref(false);
const currentAnimation = ref(props.animation);

const dialogEl = useTemplateRef("dialogEl");

function onClick(ev: MouseEvent) {
    if (ev.target === dialogEl.value && props.dismissable) {
        closed.value = true;
    }
}

function innerCancel() {
    closed.value = true;
}

function innerClosed(options?: {
    animation?: (typeof dialogAnimations)[number];
}) {
    if (options?.animation) {
        currentAnimation.value = options.animation;
    }

    closed.value = true;
}

if (typeof props.anchor === "object" && props.anchor.kind === "around-point") {
    const { width, height } = useElementSize(dialogEl);
    const anchor = props.anchor;

    watchEffect(() => {
        const w = width.value;
        const h = height.value;
        const el = dialogEl.value;

        if (el == null) {
            return;
        }

        const placement = placeRectBox({
            anchorBox: {
                x: anchor.x,
                y: anchor.y,
                width: anchor.anchorWidth ?? 0,
                height: anchor.anchorHeight ?? 0,
            },
            innerBox: {
                x: 0,
                y: 0,
                width: w,
                height: h,
            },
            inset: 20,
            containingBox: {
                x: 0,
                y: 0,
                width: window.innerWidth,
                height: window.innerHeight,
            },
            align: anchor.align ?? "center",
            position: anchor.position,
        });

        const frameId = requestAnimationFrame(() => {
            el.style.transformOrigin = placement.transformOrigin;
            el.style.left = placement.left;
            el.style.top = placement.top;
        });

        onWatcherCleanup(() => {
            cancelAnimationFrame(frameId);
        });
    });
}

const slot = defineSlots<{
    default(
        props: {},
        context: SetupContext<{
            close(): void;
            cancel(): void;
        }>,
    ): any;
}>();

onMounted(() => {
    if (props.autoshow) {
        dialogEl.value?.showModal();
    }
});

defineEmits<{
    dialogClose: [];
}>();
</script>

<style lang="scss">
.dialog-enter-from {
    &::backdrop {
        opacity: 0;
    }

    opacity: var(--dialog-initial-opacity);
    transform: var(--dialog-initial-transform);
}

.dialog-leave-to {
    &::backdrop {
        opacity: 0;
    }

    pointer-events: none;
    opacity: var(--dialog-final-opacity, var(--dialog-initial-opacity));
    transform: var(--dialog-final-transform, var(--dialog-initial-transform));
}

.dialog-a-drawer {
    --duration: 200ms;
    --dialog-initial-transform: translateY(100%);
    --dialog-transition: transform var(--duration) cubic-bezier(0, 0.5, 0, 1);

    &.anchor-left {
        --dialog-initial-transform: translateX(-100%);
    }

    &.anchor-right {
        --dialog-initial-transform: translateX(100%);
    }
}

.dialog-a-send {
    --duration: 300ms;
    --dialog-transition:
        opacity var(--duration) ease-in,
        transform var(--duration) cubic-bezier(0.5, -0.5, 1, 0.5);
    --dialog-final-opacity: 0;
    --dialog-final-transform: translateY(-300px);
}

.dialog-a-modal {
    --duration: 200ms;
    --dialog-initial-opacity: 0;
    --dialog-initial-transform: scale(0.9);
    --dialog-transition: opacity var(--duration), transform var(--duration);

    transform-origin: center;
}

.dialog-a-popup {
    --duration: 200ms;
    --dialog-initial-opacity: 0;
    --dialog-initial-transform: scale(0.9);
    --dialog-transition:
        opacity var(--duration) cubic-bezier(0, 0, 0.2, 1),
        transform var(--duration) cubic-bezier(0, 0, 0.2, 1);

    transform-origin: center;
}
</style>

<style scoped lang="scss">
.mag-dialog {
    box-sizing: border-box;
    --dialog-inset: 12px;
    padding: var(--dialog-inset);
    border: 0;
    max-width: 100%;
    transition: var(--dialog-transition);
    background: none;

    &.anchor-top {
        inset: calc(env(safe-area-inset-top, 0) + 50px) 0 auto 0;
    }

    &.anchor-bottom {
        inset: auto 0 calc(env(safe-area-inset-bottom, 0) + 50px) 0;
    }

    &.anchor-left {
        inset: env(safe-area-inset-top, 0) auto env(safe-area-inset-bottom, 0)
            calc(env(safe-area-inset-left, 0));
        max-height: calc(
            100vh - env(safe-area-inset-top, 0) - env(safe-area-inset-bottom, 0)
        );
        padding: 0;
        margin: 0;
    }

    &.anchor-right {
        inset: env(safe-area-inset-top, 0) calc(env(safe-area-inset-right, 0))
            env(safe-area-inset-bottom, 0) auto;
        max-height: calc(
            100vh - env(safe-area-inset-top, 0) - env(safe-area-inset-bottom, 0)
        );
        padding: 0;
        margin: 0;
    }

    &.anchor-dynamic {
        inset: auto;
        max-height: calc(
            100vh - env(safe-area-inset-top, 0) - env(safe-area-inset-bottom, 0)
        );
        padding: 0;
        margin: 0;
    }

    &::backdrop {
        transition: opacity var(--duration);
        background: var(--modalBg);
        backdrop-filter: var(--modalBgFilter);
    }

    &.low-backdrop::backdrop {
        opacity: 0;
    }

    > :deep(.mag-dialog-child) {
        box-sizing: border-box;

        --horizontal-area-min-inset: calc(
            env(safe-area-inset-left, 0) + env(safe-area-inset-right, 0)
        );
        --horizontal-inset: 2 * 12px;
        --width: calc(
            100vw - var(--horizontal-area-min-inset) - var(--horizontal-inset)
        );

        width: var(--width);
        border: 1px solid var(--accentedBg);

        :where(&) {
            max-width: 100%;
        }
    }

    &.no-border > :deep(.mag-dialog-child) {
        border: none;
    }

    &.mag-dialog-popup > :deep(.mag-dialog-child) {
        background-color: var(--panel);
        border-radius: var(--radius);
        contain: paint;
        box-shadow: 0 2px 5px var(--shadow);
    }
}
</style>
